<script setup lang="ts" generic="E extends { toString(): string }, T extends Array<E>">
import { api } from "@/api";
import { PV } from "@/widgets";
import { useDebounceFn, whenever } from "@vueuse/core";
import { cloneDeep, isFunction, isNotNil, isString } from "es-toolkit";
import type { SelectFilterEvent } from "primevue";
import { computed, onMounted, ref, toValue, watch, watchEffect } from "vue";
import type { SelectParam } from "../../common/types.common";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormExt, FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

interface OptionType {
    label: string;
    value: E;
}

type OptionFn = (param?: SelectParam) => Promise<OptionType[]>;

type ModelType = NonNullable<T>;
interface PropsType extends BaseProps<ModelType> {
    options: OptionFn | OptionType[] | string;
    formId?: string;
    searchable?: boolean;
    placeholder?: string;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    value: undefined,
    searchable: false,
});

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state, {
    onCancel() {
        state.hasError = false;
        state.editMode = false;
        model.value = cloneDeep(toValue(state.original)) as T;
        filterValue.value = undefined;
    },
    onScramble() {
        if (state.editMode && state.fn.scramble !== undefined) {
            const choices = [...choiceList.value.values()];
            model.value = state.fn.scramble(choices.map((c) => c.value));
        }
    },
});

const choiceList = ref<OptionType[]>([]);
const virtualScrollerOptions = computed(() => (choiceList.value.length > 20 ? { itemSize: 42 } : undefined));

const showLoading = ref(false);
const isFiltering = ref(false);
const filterValue = ref<string>();

const emptyMessage = computed(() => {
    return showLoading.value === false ? "Tidak ada pilihan." : "Sedang memuat...";
});
const emptyFilterMessage = computed(() => {
    return isFiltering.value === false ? "Tidak ada pilihan." : "Sedang mencari...";
});

async function populateOptions() {
    let data: OptionType[];

    if (isFunction(props.options)) {
        showLoading.value = true;
        const fn = props.options as OptionFn;
        data = await fn({
            search: filterValue.value,
            current: model.value?.map((e) => e.toString()).join(","),
        });
        showLoading.value = false;
    } else if (isString(props.options)) {
        const req = api.raw.get<OptionType[]>(props.options);
        data = (await req.execute()) ?? [];
    } else {
        data = props.options as OptionType[];
    }

    choiceList.value = data;
    isFiltering.value = false;
}

async function onFilter(event: SelectFilterEvent) {
    const filter = event.value;
    if (isNotNil(filter) && filter.trim().length > 0) {
        filterValue.value = filter.trim();
    } else {
        filterValue.value = undefined;
    }

    isFiltering.value = true;
}

const refreshFn = useDebounceFn(populateOptions, 500, {
    maxWait: 2000,
    rejectOnCancel: false,
});

watch(filterValue, refreshFn);
watch(() => props.options, populateOptions, { immediate: false });

if (!state.editMode) {
    whenever(() => model.value !== undefined, populateOptions, { once: true, immediate: true });
} else {
    onMounted(populateOptions);
}

watchEffect(() => {
    if (model.value === null || model.value?.length === 0) {
        model.value = undefined;
    }
});
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <PV.IconField>
                <div
                    v-if="showLoading && model !== undefined"
                    class="rns-g-static-input px-1 py-3"
                    :class="{ 'pr-10': state.updatable }"
                >
                    <PV.Skeleton />
                </div>
                <PV.MultiSelect
                    v-else
                    v-model="model"
                    class="rns-input-static"
                    disabled
                    fluid
                    optionLabel="label"
                    :options="choiceList"
                    optionValue="value"
                />
                <FormExt :actions="actions" :state="state" />
            </PV.IconField>
        </template>
        <template v-else>
            <PV.MultiSelect
                v-model="model"
                :disabled="props.disabled"
                :emptyFilterMessage="emptyFilterMessage"
                :emptyMessage="emptyMessage"
                :filter="props.searchable"
                fluid
                :form="props.formId"
                :inputId="state.id"
                :invalid="state.hasError"
                optionLabel="label"
                :options="choiceList"
                optionValue="value"
                :placeholder="props.placeholder ?? `Pilih ${label}`"
                :required="props.required"
                :showClear="state.clearable"
                :virtualScrollerOptions="virtualScrollerOptions"
                @beforeShow="populateOptions"
                @filter="onFilter"
            >
                <template #clearicon>
                    <Buttons.BtnReset @click="actions.onReset" />
                </template>
                <template v-if="showLoading" #dropdownicon>
                    <i class="pi pi-spin pi-spinner"></i>
                </template>
            </PV.MultiSelect>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-input-static {
    @apply text-gray-700! bg-transparent! shadow-none!;
    @apply rns-g-static-input;
}

.rns-input-static :deep(.p-multiselect-dropdown-icon) {
    @apply hidden!;
}

.rns-input-static :deep(.p-multiselect-label) {
    @apply text-gray-700! px-1! pt-2.25!;
}
</style>
